import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Step1Intro from './steps/Step1Intro';
import Step2Age from './steps/Step2Age';
import Step3Ethnicity from './steps/Step3Ethnicity';
import Step4BodyType from './steps/Step4BodyType';
import Step5BreastSize from './steps/Step5BreastSize';
import Step6ButtSize from './steps/Step6ButtSize';
import Step7HairColor from './steps/Step7HairColor';
import Step8Features from './steps/Step8Features';
import Step9SpicyPhotos from './steps/Step9SpicyPhotos';
import Step10VoiceMessages from './steps/Step10VoiceMessages';
import { ChevronLeft } from 'lucide-react';
import CryptoJS from 'crypto-js';

// JWT Token Generation
const createJWT = (payload, secret) => {
  // JWT Header
  const header = {
    alg: 'HS256',
    typ: 'JWT'
  };
  
  // Base64URL encode function
  const base64UrlEncode = (str) => {
    return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };
  
  // Encode header
  const headerEncoded = base64UrlEncode(
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(JSON.stringify(header)))
  );
  
  // Encode payload
  const payloadEncoded = base64UrlEncode(
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(JSON.stringify(payload)))
  );
  
  // Create signature
  const signatureBase = `${headerEncoded}.${payloadEncoded}`;
  const signature = CryptoJS.HmacSHA256(signatureBase, secret);
  const signatureEncoded = base64UrlEncode(CryptoJS.enc.Base64.stringify(signature));
  
  // Return complete JWT token
  return `${headerEncoded}.${payloadEncoded}.${signatureEncoded}`;
};

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    imageStyle: 'realistic',
    gender: 'female',
    safetyLevel: 'nude',
    age: 25,
    extraFields: {
      ethnicity: '',
      bodyType: '',
      breastSize: '',
      buttSize: '',
      hairColor: '',
      features: [],
      spicyPhotos: '',
      voiceMessages: ''
    }
  });

  useEffect(() => {
    // Load from sessionStorage if exists
    const saved = sessionStorage.getItem('onboardingData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed.formData);
        setCurrentStep(parsed.currentStep);
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Save to sessionStorage
    sessionStorage.setItem('onboardingData', JSON.stringify({ formData, currentStep }));
  }, [formData, currentStep]);

  const handleNext = (data) => {
    const updatedData = {
      ...formData,
      ...data
    };
    
    setFormData(updatedData);
    
    if (currentStep === 10) {
      // Generate JWT and redirect after Step 10
      generateJWTAndRedirect(updatedData);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const generateJWTAndRedirect = (finalData) => {
    try {
      // JWT Secret Key
      const JWT_SECRET = '2u0O4I&>m|n|5P"Nwvi0SlimSteam#';
      
      // Affiliate ID
      const AFFILIATE_ID = '847e9325a3b39b98e2a56ad49c76af2b3e064c81f7bae1a6f8173a74c292fffc';
      
      // Campaign ID
      const CAMPAIGN_ID = 'get_honey';
      
      // Source ID
      const SOURCE_ID = 'yourgirl-lovescape-space';
      
      // Remove spicyPhotos and voiceMessages from JWT payload
      const jwtPayload = {
        imageStyle: finalData.imageStyle,
        gender: finalData.gender,
        safetyLevel: finalData.safetyLevel,
        age: finalData.age,
        extraFields: {
          ethnicity: finalData.extraFields.ethnicity,
          bodyType: finalData.extraFields.bodyType,
          breastSize: finalData.extraFields.breastSize,
          buttSize: finalData.extraFields.buttSize,
          hairColor: finalData.extraFields.hairColor,
          features: finalData.extraFields.features
        }
      };
      
      // Generate JWT token
      const token = createJWT(jwtPayload, JWT_SECRET);
      
      console.log('JWT Payload (without spicyPhotos/voiceMessages):', jwtPayload);
      console.log('Generated JWT:', token);
      
      // Build redirect URL: https://lovescape.com/character-pro/avatar/JWT?userId=affiliateID&campaignId=CAMPAIGN_ID&sourceId=SOURCE_ID
      const redirectUrl = `https://lovescape.com/character-pro/avatar/${token}?userId=${AFFILIATE_ID}&campaignId=${CAMPAIGN_ID}&sourceId=${SOURCE_ID}`;
      
      console.log('Redirecting to:', redirectUrl);
      
      // Perform redirect
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('JWT Generation Error:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const steps = {
    1: <Step1Intro onNext={handleNext} />,
    2: <Step2Age onNext={handleNext} currentData={formData} />,
    3: <Step3Ethnicity onNext={handleNext} currentData={formData} />,
    4: <Step4BodyType onNext={handleNext} currentData={formData} />,
    5: <Step5BreastSize onNext={handleNext} currentData={formData} />,
    6: <Step6ButtSize onNext={handleNext} currentData={formData} />,
    7: <Step7HairColor onNext={handleNext} currentData={formData} />,
    8: <Step8Features onNext={handleNext} currentData={formData} />,
    9: <Step9SpicyPhotos onNext={handleNext} currentData={formData} />,
    10: <Step10VoiceMessages onNext={handleNext} currentData={formData} />
  };

  return (
    <div className="onboarding-container">
      {currentStep > 1 && (
        <button 
          onClick={handleBack}
          className="back-button"
          aria-label="Go back"
        >
          <ChevronLeft size={28} />
        </button>
      )}
      
      <div className="step-content">
        {steps[currentStep]}
      </div>

      {currentStep > 1 && (
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentStep / 10) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default Onboarding;